import type { Option } from '@byecode/ui'
import { Breadcrumbs, Divider, Flex, IconFont, singleTextEllipsis } from '@byecode/ui'
import type { FilterFormType } from '@lighthouse/core'
import {
    type DataSourceAbstract,
    DataSourceType,
    type FilterBlockAbstract,
    VariableType,
    type ViewField,
    type ViewFieldProtocol
} from '@lighthouse/core'
import { DEFAULT_VALUE_VARIABLE, FieldItemAdder, FILTER_OPERATOR, ListItem4ByecodeUi, useAtomAction } from '@lighthouse/shared'
import { findIndex } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useToggle } from 'react-use'
import styled from 'styled-components'

import { blockHighlightAtom } from '@/atoms/page/state'
import { useBlockSettingContext } from '@/contexts/BlockSettingContext'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { InnerDrawer } from '../../InnerDrawer'
import type { ViewBaseSlice } from '../../View/formSlice'
import { FilterControlItem } from './FilterControlItem'

const StyledText = styled.div<{ color?: string }>`
    color: ${({ color }) => color};
    font-size: 14px;
    line-height: 16px;
    margin-right: 2px;
    ${singleTextEllipsis()}
`

const SCxFilterControlContainer = styled.div`
    padding: 16px;
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

interface FilterControlConfigureProps {
    linkFilter?: FilterFormType
    columns: ViewFieldProtocol[]
    filterBlocks?: FilterBlockAbstract[]
    dataSource: DataSourceAbstract
    onBack: () => void
}

type LinkFilterConditionField = {
    id: string
    idVariable?: {
        fieldIdVariable?: {
            fieldId?: string
        }
    }
}

const FilterControlConfigure: React.FC<FilterControlConfigureProps> = ({ linkFilter, columns, filterBlocks, dataSource, onBack }) => {
    const { scope } = useBlockSettingContext()
    const { schema, type } = dataSource
    const { control, setValue } = useFormContext<ViewBaseSlice>()
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'linkFilterController.expression.conditions'
    })
    const conditionFields = fields as LinkFilterConditionField[]

    const isShowDsName = type === DataSourceType.joinDataSource
    const disabledWithVersion = useIsDisabledWithVersion()
    const handleMouseEnter = useCallback(
        (id: string) => {
            if (!scope) {
                setBlockHighlight([id])
                document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                return
            }
            setBlockHighlight([id])
            document
                .querySelector(`div[data-node-id="${id}"][data-layout-scope="${scope}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [scope, setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const filterBlockOptions: Option[] = useMemo(() => {
        if (!filterBlocks) {
            return []
        }
        return filterBlocks?.map(item => ({
            label: item.title,
            value: item.id,
            icon: <IconFont type="Filter" size={16} color="var(--color-gray-400)" />,
            onMouseEnter: () => handleMouseEnter(item.id),
            onMouseLeave: () => handleMouseLeave()
        }))
    }, [filterBlocks, handleMouseEnter, handleMouseLeave])

    const limitColumns = useMemo(() => {
        return columns.filter(item => item.innerType !== 'NULL' && item.type !== 'notes' && item.type !== 'file' && item.type !== 'video')
    }, [columns])

    const selectedFieldIds = useMemo(() => {
        return (
            conditionFields.reduce<string[]>((prev, cur) => {
                const fieldId = cur?.idVariable?.fieldIdVariable?.fieldId
                if (fieldId) {
                    prev.push(fieldId)
                }
                return prev
            }, []) || []
        )
    }, [conditionFields])

    const handleAdd = useCallback(
        (id: string) => {
            if (!linkFilter) {
                const newLinkFilter: FilterFormType = {
                    expression: {
                        where: 'AND',
                        conditions: [
                            {
                                idVariable: {
                                    type: VariableType.FIELD_ID,
                                    fieldIdVariable: { fieldId: id }
                                },
                                operator: FILTER_OPERATOR,
                                paramList: [DEFAULT_VALUE_VARIABLE]
                            }
                        ]
                    }
                }

                setValue('linkFilterController', newLinkFilter)
                return
            }

            if (!linkFilter.expression?.where) {
                const newLinkFilter: FilterFormType = {
                    expression: {
                        where: 'AND',
                        conditions: [
                            ...(linkFilter.expression?.conditions || []),
                            {
                                idVariable: {
                                    type: VariableType.FIELD_ID,
                                    fieldIdVariable: { fieldId: id }
                                },
                                operator: FILTER_OPERATOR,
                                paramList: [DEFAULT_VALUE_VARIABLE]
                            }
                        ]
                    }
                }
                setValue('linkFilterController', newLinkFilter)
                return
            }

            append({
                idVariable: {
                    type: VariableType.FIELD_ID,
                    fieldIdVariable: { fieldId: id }
                },
                operator: FILTER_OPERATOR,
                paramList: [DEFAULT_VALUE_VARIABLE]
            })
        },
        [append, linkFilter, setValue]
    )

    const handleDelete = useCallback(
        (id: string) => {
            const index = findIndex(item => item.id === id, fields)
            remove(index)
        },
        [fields, remove]
    )

    return (
        <InnerDrawer style={{ display: 'flex', flexDirection: 'column' }}>
            <Breadcrumbs items={[{ icon: <IconFont type="ArrowLeft" />, label: '联动筛选', onClick: onBack }]} />
            <Divider color="var(--color-gray-200)" />
            <SCxFilterControlContainer>
                {conditionFields.map((condition, index) => {
                    const fieldId = condition.idVariable?.fieldIdVariable?.fieldId
                    if (!fieldId) {
                        return null
                    }
                    return (
                        <FilterControlItem
                            key={fieldId}
                            id={condition.id}
                            fieldId={fieldId}
                            schema={schema}
                            disabled={disabledWithVersion}
                            filterBlockOptions={filterBlockOptions}
                            filterBlocks={filterBlocks}
                            onDelete={handleDelete}
                            prefixName={`linkFilterController.expression.conditions.${index}`}
                        />
                    )
                })}
                <FieldItemAdder
                    text="添加"
                    style={{ padding: 0 }}
                    isShowDsName={isShowDsName}
                    // noSettingFields={noSettingFields}
                    onAdd={handleAdd}
                    columns={limitColumns}
                    selectedFieldIds={selectedFieldIds}
                />
            </SCxFilterControlContainer>
        </InnerDrawer>
    )
}

interface FilterControlProps {
    dataSource: DataSourceAbstract
    columns: ViewField[]
    filterBlocks?: FilterBlockAbstract[]
    enableHover?: boolean
    disableIcon?: boolean
    style?: React.CSSProperties
}

export const FilterControl: React.FC<FilterControlProps> = ({ dataSource, columns, filterBlocks, enableHover, disableIcon, style }) => {
    const { control } = useFormContext()
    const linkFilter = useWatch({ control, name: 'linkFilterController' })
    const [open, toggle] = useToggle(false)
    const text = useMemo(() => {
        const length = linkFilter?.expression?.conditions?.length ?? 0
        if (length > 0) {
            return `${length} 条筛选项`
        }
        return '未设置'
    }, [linkFilter?.expression?.conditions?.length])

    return (
        <>
            <ListItem4ByecodeUi
                justifyContent="space-between"
                alignItems="center"
                enableHover={enableHover}
                onClick={() => toggle(true)}
                style={style}
            >
                <Flex style={{ overflow: 'hidden' }} alignItems="center">
                    {!disableIcon && (
                        <Flex style={{ color: 'var(--color-gray-400)', marginRight: 8 }}>
                            <IconFont size={16} type="Binding" />
                        </Flex>
                    )}
                    <StyledText>联动筛选</StyledText>
                </Flex>
                <Flex alignItems="center" style={{ overflow: 'hidden', cursor: 'pointer' }}>
                    <StyledText color="var(--color-gray-400)">{text}</StyledText>
                    <IconFont color="var(--color-gray-400)" size={16} type="ArrowRightSmall" />
                </Flex>
            </ListItem4ByecodeUi>

            {open && (
                <FilterControlConfigure
                    linkFilter={linkFilter}
                    columns={columns}
                    filterBlocks={filterBlocks}
                    dataSource={dataSource}
                    onBack={() => toggle(false)}
                />
            )}
        </>
    )
}
